import React from "react";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { ArrowForward, Storage } from "@mui/icons-material";
import { translate } from "../../../../translate/translate";
import { useSapIntegrationHealth } from "../../../../hooks/useSapIntegrationHealth";
import { SapImportHealth } from "../../../../models/general/sap-integration-health";
import {
  formatImportDate,
  formatImportDuration,
  getImportResultMessage,
  getOverallImportStatus,
} from "../../../../utils/sap-integration-health-utils";
import IntegrationStatusBadgeComponent from "./integration-status-badge.component";

const MOCK_FONT_FAMILY = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const cardShellSx = {
  backgroundColor: "white",
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
  fontFamily: MOCK_FONT_FAMILY,
  padding: "20px",
  width: "100%",
};

const gridSx = {
  display: "grid",
  gap: "12px",
  marginTop: "16px",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    md: "repeat(2, minmax(0, 1fr))",
    xl: "repeat(4, minmax(0, 1fr))",
  },
};

const SapImportCard = ({ importHealth }: { importHealth: SapImportHealth }) => {
  const navigate = useNavigate();
  const name = translate.t(importHealth.nameKey);

  return (
    <Box
      component="button"
      type="button"
      onClick={() => navigate(importHealth.detailsUrl)}
      aria-label={`${translate.t("view_details")}: ${name}`}
      sx={{
        backgroundColor: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        cursor: "pointer",
        display: "block",
        fontFamily: MOCK_FONT_FAMILY,
        padding: "16px",
        textAlign: "left",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        width: "100%",
        "&:hover": { borderColor: "#1e3f7a", boxShadow: "0 4px 10px rgba(0, 0, 0, 0.08)" },
        "&:focus-visible": { outline: "2px solid #1e3f7a", outlineOffset: "2px" },
      }}
    >
      <Box sx={{ alignItems: "flex-start", display: "flex", justifyContent: "space-between", gap: "8px", width: "100%" }}>
        <Typography sx={{ fontFamily: MOCK_FONT_FAMILY, fontSize: "13px", fontWeight: 600, color: "#1f2937" }}>
          {name}
        </Typography>
        <IntegrationStatusBadgeComponent status={importHealth.status} compact />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "14px", width: "100%" }}>
        <Box sx={{ width: "auto" }}>
          <Typography sx={{ fontFamily: MOCK_FONT_FAMILY, fontSize: "10px", textTransform: "uppercase", color: "#9ca3af" }}>
            {translate.t("last_execution")}
          </Typography>
          <Typography sx={{ fontFamily: MOCK_FONT_FAMILY, fontSize: "12px", fontWeight: 500, color: "#374151", marginTop: "2px" }}>
            {formatImportDate(importHealth)}
          </Typography>
        </Box>
        <Box sx={{ width: "auto" }}>
          <Typography sx={{ fontFamily: MOCK_FONT_FAMILY, fontSize: "10px", textTransform: "uppercase", color: "#9ca3af" }}>
            {translate.t("duration")}
          </Typography>
          <Typography sx={{ fontFamily: MOCK_FONT_FAMILY, fontSize: "12px", fontWeight: 500, color: "#374151", marginTop: "2px" }}>
            {formatImportDuration(importHealth)}
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: MOCK_FONT_FAMILY,
          fontSize: "12px",
          color: "#6b7280",
          marginTop: "12px",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {getImportResultMessage(importHealth)}
      </Typography>

      <Box sx={{ alignItems: "center", display: "inline-flex", gap: "4px", marginTop: "10px", width: "auto" }}>
        <Typography sx={{ fontFamily: MOCK_FONT_FAMILY, fontSize: "12px", color: "#1e3f7a", fontWeight: 500 }}>
          {translate.t("view_details")}
        </Typography>
        <ArrowForward sx={{ fontSize: "11px", color: "#1e3f7a" }} />
      </Box>
    </Box>
  );
};

const HealthSkeleton = () => (
  <Box sx={cardShellSx}>
    <Box sx={{ height: "18px", width: "260px", backgroundColor: "#e5e7eb", borderRadius: "4px", animation: `${pulse} 1.5s ease-in-out infinite` }} />
    <Box sx={gridSx}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            height: "144px",
            borderRadius: "8px",
            backgroundColor: "#f3f4f6",
            animation: `${pulse} 1.5s ease-in-out infinite`,
          }}
        />
      ))}
    </Box>
  </Box>
);

interface SapIntegrationHealthComponentProps {
  /** Id of the analysis currently selected on the Dashboard, used to build the Preço por PN card's destination. */
  analysisId?: number | null;
}

const SapIntegrationHealthComponent = ({ analysisId }: SapIntegrationHealthComponentProps = {}) => {
  const { imports, isLoading, isError, refetch } = useSapIntegrationHealth(analysisId);
  const overallStatus = getOverallImportStatus(imports);

  if (isLoading) return <HealthSkeleton />;

  if (isError) {
    return (
      <Box sx={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "12px", padding: "20px", width: "100%" }}>
        <Typography sx={{ fontFamily: MOCK_FONT_FAMILY, fontSize: "14px", fontWeight: 600, color: "#b91c1c" }}>
          {translate.t("sap_import_health_load_error")}
        </Typography>
        <Button
          onClick={refetch}
          variant="contained"
          sx={{ backgroundColor: "#dc2626", fontSize: "12px", marginTop: "12px", textTransform: "none", "&:hover": { backgroundColor: "#b91c1c" } }}
        >
          {translate.t("try_again")}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={cardShellSx}>
      <Box sx={{ alignItems: "flex-start", display: "flex", justifyContent: "space-between", gap: "16px", width: "100%" }}>
        <Box sx={{ alignItems: "center", display: "flex", gap: "8px", width: "auto" }}>
          <Storage sx={{ color: "#1e3f7a", fontSize: "17px" }} />
          <Box sx={{ width: "auto" }}>
            <Typography sx={{ fontFamily: MOCK_FONT_FAMILY, fontSize: "14px", fontWeight: 600, color: "#374151" }}>
              {translate.t("sap_import_health")}
            </Typography>
            <Typography sx={{ fontFamily: MOCK_FONT_FAMILY, fontSize: "12px", color: "#9ca3af", marginTop: "2px" }}>
              {translate.t("sap_import_health_subtitle")}
            </Typography>
          </Box>
        </Box>
        {overallStatus === "critical" && <IntegrationStatusBadgeComponent status={overallStatus} />}
      </Box>

      <Box sx={gridSx}>
        {imports.map(importHealth => (
          <SapImportCard key={importHealth.id} importHealth={importHealth} />
        ))}
      </Box>
    </Box>
  );
};

export default SapIntegrationHealthComponent;
